import type { LibraWorkflow } from 'features/libra/workflow/workflowStepsApi'

type WorkflowStep = LibraWorkflow['steps'][number]

/**
 * Keys inside a step/section config that point to ontology field ids.
 *
 * There is no single enum declaring these across the codebase — they are
 * spread across the section schemas listed below. If you add a new section
 * family (or a new field-id-bearing key on an existing one), add the key to
 * the relevant union type here so the `ReadonlySet` stays in sync with the
 * schema at the type level.
 *
 * Covered section schemas:
 *   - FormSection       ({ fields: string[] })
 *     → features/libra/sections/formSection/formSectionTypes.ts
 *   - FlowchartSection  ({ fieldId?: string })
 *     → features/libra/sections/flowchartSection/flowchartTypes.ts
 *   - ArrayTableSection ({ field: string, fields?: string[] })
 *     → features/libra/sections/arrayTableSection/arrayTableSectionTypes.ts
 *   - ComputedSection   ({ fieldId?, overrideField?, overrideJustificationField?,
 *                          displayFields: string[], functionSpec.parameters[].field })
 *     → features/libra/sections/computedSection/computedSectionTypes.ts
 */
type StringFieldKey =
  | 'field'
  | 'fieldId'
  | 'overrideField'
  | 'overrideJustificationField'

type StringArrayFieldKey = 'fields' | 'displayFields'

const STRING_FIELD_KEYS: ReadonlySet<StringFieldKey> = new Set<StringFieldKey>([
  'field',
  'fieldId',
  'overrideField',
  'overrideJustificationField',
])

const STRING_ARRAY_FIELD_KEYS: ReadonlySet<StringArrayFieldKey> =
  new Set<StringArrayFieldKey>(['fields', 'displayFields'])

/**
 * Walks a workflow step's `config` and returns the de-duplicated list of
 * ontology field ids it references. Intentionally heuristic — unrecognised
 * shapes fall through silently so the caller can bucket unmapped fields into
 * an "Other" group.
 */
export function collectStepFieldIds(step: WorkflowStep): string[] {
  const stepFieldIds = new Set<string>()
  collectFieldIdsFromNode(step.config, stepFieldIds)
  return [...stepFieldIds]
}

function collectFieldIdsFromNode(
  node: unknown,
  stepFieldIds: Set<string>
): void {
  if (node === null || node === undefined) return

  if (Array.isArray(node)) {
    for (const child of node) collectFieldIdsFromNode(child, stepFieldIds)
    return
  }

  if (typeof node !== 'object') return

  for (const [key, value] of Object.entries(node as Record<string, unknown>)) {
    if (isStringFieldKey(key) && typeof value === 'string' && value) {
      stepFieldIds.add(value)
      continue
    }

    if (isStringArrayFieldKey(key) && Array.isArray(value)) {
      for (const item of value) {
        if (typeof item === 'string' && item) stepFieldIds.add(item)
      }
      continue
    }

    collectFieldIdsFromNode(value, stepFieldIds)
  }
}

function isStringFieldKey(key: string): key is StringFieldKey {
  return STRING_FIELD_KEYS.has(key as StringFieldKey)
}

function isStringArrayFieldKey(key: string): key is StringArrayFieldKey {
  return STRING_ARRAY_FIELD_KEYS.has(key as StringArrayFieldKey)
}
